// QMO integration client — talks to the external Quiet Money
// Opportunity (QMO) app at survey.haevn.co. This is a browser-side
// fetch client only; there is no HAEVN backend involvement. If the
// QMO endpoints change host or path, update the constant below.
//
// The two endpoints we consume:
//   GET /api/qmo-settings
//       → Full config bundle (categories, cities, tiers, pricing, ...)
//         fetched once at app load and cached in QmoConfigProvider.
//
//   GET /api/qmo-score?address=<street>&category=<qmo_category>
//       → Per-prospect score. Fetched every time a prospect record is
//         opened; NOT cached. 404 is a valid response — the QMO app
//         returns a human-readable `message` (or `detail`) explaining
//         why the score couldn't be computed (address unrecognized,
//         category unsupported in that geography, etc.).

export const QMO_BASE = "https://survey.haevn.co/api";

export class QmoNotFoundError extends Error {
  constructor(message) {
    super(message);
    this.name = "QmoNotFoundError";
    this.status = 404;
  }
}

// Small helper — the QMO endpoints are HTML-shell-safe (SPA fallback
// returns text/html), so we sanity-check the content-type before we
// try to parse. Anything that isn't JSON is treated as "endpoint not
// live yet" and surfaced as a plain Error the caller can display.
async function _parseJsonOrThrow(res) {
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    throw new Error("QMO API returned a non-JSON response.");
  }
  return res.json();
}

// Best-effort human message extractor for QMO 404s. The QMO app is
// expected to return one of {message, detail, error} in JSON. If
// nothing matches we fall back to a static message.
function _extractMessage(body, fallback) {
  if (!body || typeof body !== "object") return fallback;
  return body.message || body.detail || body.error || fallback;
}

export async function fetchQmoSettings({ signal } = {}) {
  const res = await fetch(`${QMO_BASE}/qmo-settings`, {
    method: "GET",
    signal,
    // No credentials — QMO settings endpoint is expected to be public
    // or shared-secret gated on their side. Sending credentials would
    // force a stricter CORS preflight.
    credentials: "omit",
  });
  if (!res.ok) {
    throw new Error(`QMO settings request failed (HTTP ${res.status}).`);
  }
  return _parseJsonOrThrow(res);
}

export async function fetchQmoScore({ address, category, signal }) {
  const params = new URLSearchParams();
  if (address) params.set("address", address);
  if (category) params.set("category", category);
  const res = await fetch(`${QMO_BASE}/qmo-score?${params.toString()}`, {
    method: "GET",
    signal,
    credentials: "omit",
  });
  if (res.status === 404) {
    // Try to surface the human message from the response body.
    let body = null;
    try { body = await res.json(); } catch { /* not JSON — fall through */ }
    throw new QmoNotFoundError(
      _extractMessage(body, "No QMO score available for this prospect.")
    );
  }
  if (!res.ok) {
    throw new Error(`QMO score request failed (HTTP ${res.status}).`);
  }
  return _parseJsonOrThrow(res);
}
